import Phaser from 'phaser';
import { Direction, EntityKind, PvpMark, TILE_SIZE, type EntityState } from '@game/protocol';

import {
  CHARACTER_ART_HEIGHT,
  characterAnimationKey,
  characterTextureKey,
  type CharacterAnimation,
} from '../art/characters.ts';
import {
  creatureAnimationKey,
  creatureArtHeight,
  creatureTextureKey,
} from '../art/creatures.ts';
import { EntityView } from './entity-view.ts';

const TARGET_RING_COLOR = 0xe0a33e;
const BAR_WIDTH = TILE_SIZE - 8;
const BAR_HEIGHT = 3;

/** Respiro entre corpo e nome, entre nome e barra, e entre barra e balão. */
const STACK_GAP = 2;

/**
 * Teto de elevação no desempilhamento de rótulos.
 *
 * Acima de ~2 tiles o nome deixa de ser lido como pertencente à criatura e vira legenda
 * solta. Num amontoado maior que isto, o excedente volta ao degrau zero: devolve o defeito
 * antigo para o quarto nome, o que é ruim, mas menos confuso que um nome flutuando longe
 * do dono.
 */
const MAX_LABEL_ROWS = 3;

/** Folga horizontal entre dois rótulos: colados, dois nomes leem como uma palavra só. */
const LABEL_GAP_X = 4;

/**
 * Cor da marca de PvP (PRD §6).
 *
 * Branco para agressor, vermelho para assassino — a mesma leitura de gravidade que o resto
 * da interface já usa nas mensagens (`MessageKind.Warn` contra `MessageKind.Loss`) e na
 * barra de vida. O losango é desenhado ao lado do nome, e não sobre o corpo, porque o corpo
 * já carrega barra de vida, anel de alvo e balão: mais um objeto sobre ele competiria com
 * as três coisas que mudam durante a briga.
 */
const MARK_COLORS: Readonly<Record<PvpMark, number>> = {
  [PvpMark.None]: 0x000000,
  [PvpMark.Aggressor]: 0xf4f7fa,
  [PvpMark.Murderer]: 0xd9534f,
};

const MARK_SIZE = 7;

/**
 * Profundidade de desenho a partir da linha da **grade**, não do pixel interpolado.
 *
 * Usar o pixel faria a ordem trocar no meio de um passo e dois personagens vizinhos
 * piscariam um por cima do outro (PRD §8.2). A linha da grade só muda quando o passo
 * termina, que é exatamente quando a ordem deve mudar.
 */
function depthFor(gridY: number, bonus = 0): number {
  return gridY * 10 + bonus;
}

/**
 * Tudo que se desenha de uma criatura: corpo animado, nome, barra de vida, anel de alvo
 * e balão de fala.
 *
 * Agrupado numa classe porque os cinco precisam se mover juntos a cada quadro. Soltos
 * num mapa por tipo de objeto, mais cedo ou mais tarde sobraria uma barra de vida na
 * tela depois que o corpo já tivesse sido destruído.
 */
export class CreatureSprite {
  readonly view: EntityView;

  private readonly scene: Phaser.Scene;
  private readonly body: Phaser.GameObjects.Sprite;
  private readonly label: Phaser.GameObjects.Text;
  private readonly barBackground: Phaser.GameObjects.Rectangle;
  private readonly barFill: Phaser.GameObjects.Rectangle;
  private readonly targetRing: Phaser.GameObjects.Ellipse;
  private readonly markIcon: Phaser.GameObjects.Rectangle;

  private bubble: Phaser.GameObjects.Text | undefined;
  private bubbleTimer: Phaser.Time.TimerEvent | undefined;

  private readonly isPlayer: boolean;
  private textureKey: string;
  private vocation: number;
  private tier: number;

  /**
   * Do chão ao topo da cabeça, em pixels — é daqui que a pilha de rótulos sobe.
   *
   * Era uma constante: `CHARACTER_Y_OFFSET`, tirada do quadro de 48 do personagem, servia
   * para todo mundo porque toda criatura cabia no mesmo quadro. Desde que a espécie tem
   * tamanho próprio (`Outfit.scale`), não serve mais — o nome do Dragão, que tem o dobro da
   * altura de um homem, caía no meio do peito dele.
   */
  private readonly artHeight: number;

  private direction: Direction = Direction.South;
  private animation: CharacterAnimation | 'walk' = 'idle';
  /** Trava o retorno ao repouso enquanto uma animação de uso único não termina. */
  private busyUntil = 0;
  private dead = false;

  /**
   * Altura real da caixa do nome, medida pelo Phaser: o `fontSize` do metrics mais o
   * contorno (`GetTextSize`). Guardada porque é ela — e não o palpite de 10 px que estava
   * embutido nos deslocamentos — que decide onde a barra de vida cabe sem cortar as letras.
   * Num monoespaçado de 10 px com contorno de 3 dá ~16, não 10.
   */
  private labelHeight = 0;

  /** Degrau em que o desempilhamento pôs esta pilha, em pixels. Ver `layoutCreatureLabels`. */
  private labelLift = 0;

  constructor(scene: Phaser.Scene, entity: EntityState, isLocal: boolean) {
    this.scene = scene;
    this.isPlayer = entity.kind === EntityKind.Player;
    this.vocation = entity.vocation;
    this.tier = entity.outfitTier;
    this.view = new EntityView(entity.x, entity.y);

    this.textureKey = this.isPlayer
      ? characterTextureKey(entity.vocation, entity.outfitTier)
      : creatureTextureKey(entity.vocation);

    // O personagem tem uma altura só, e trocar de armadura não muda o quadro dele: medir
    // uma vez basta. Criatura não troca de espécie.
    this.artHeight = this.isPlayer
      ? CHARACTER_ART_HEIGHT
      : creatureArtHeight(entity.vocation);

    this.targetRing = scene.add.ellipse(
      this.view.renderX,
      this.view.renderY + TILE_SIZE / 2 - 3,
      TILE_SIZE - 4,
      TILE_SIZE / 2 - 2,
    );
    this.targetRing.setStrokeStyle(2, TARGET_RING_COLOR, 1);
    this.targetRing.setFillStyle();
    this.targetRing.setVisible(false);

    this.body = scene.add.sprite(this.view.renderX, this.view.renderY, this.textureKey, 0);
    // Âncora no centro-inferior: é o que faz o personagem pisar no tile em vez de
    // flutuar sobre ele (PRD §8.1).
    this.body.setOrigin(0.5, 1);

    this.label = scene.add.text(this.view.renderX, this.view.renderY, '', {
      fontFamily: 'ui-monospace, monospace',
      fontSize: '10px',
      color: isLocal ? '#7fd4a0' : this.isPlayer ? '#cfe3f5' : '#e0b08a',
      stroke: '#0b0f14',
      strokeThickness: 3,
    });
    this.label.setOrigin(0.5, 1);
    // Vale já com texto vazio: uma linha mede `fontSize + strokeThickness`. Sem isto, o
    // primeiro `sync` (chamado no fim deste construtor) posicionaria a barra por cima do
    // nada e ela saltaria no quadro em que o `EntityInfo` chegasse.
    this.labelHeight = this.label.height;

    this.barBackground = scene.add.rectangle(0, 0, BAR_WIDTH, BAR_HEIGHT, 0x000000, 0.7);
    this.barFill = scene.add.rectangle(0, 0, BAR_WIDTH, BAR_HEIGHT, 0x5cb85c);

    // Losango: um quadrado girado 45°. A forma importa mais que a cor — quem joga sem
    // distinguir bem vermelho de branco ainda vê que **há** uma marca.
    this.markIcon = scene.add.rectangle(0, 0, MARK_SIZE, MARK_SIZE, MARK_COLORS[PvpMark.Aggressor]);
    this.markIcon.setStrokeStyle(1, 0x0b0f14, 1);
    this.markIcon.setAngle(45);
    this.markIcon.setVisible(false);

    this.setHealth(1, 1);
    this.play('idle');
    this.sync();
  }

  setName(name: string): void {
    this.label.setText(name);
    // Remedido aqui, e não no `sync`: assim a altura da pilha só pode mudar quando o nome
    // muda, nunca por causa de dano. Nome que sobe e desce ao levar pancada é pior de ler
    // que a sobreposição que este conserto remove.
    this.labelHeight = this.label.height;
  }

  /** Troca de aparência sem recriar o sprite: equipar armadura muda a faixa visual. */
  setOutfit(vocation: number, tier: number): void {
    if (!this.isPlayer) return;
    if (this.vocation === vocation && this.tier === tier) return;

    this.vocation = vocation;
    this.tier = tier;
    this.textureKey = characterTextureKey(vocation, tier);
    this.body.setTexture(this.textureKey, 0);
    this.play(this.animation, true);
  }

  setDirection(direction: Direction): void {
    if (this.direction === direction) return;
    this.direction = direction;
    this.play(this.animation, true);
  }

  /** Caminhando ou parado, decidido pela interpolação em curso. */
  updateAnimation(): void {
    if (this.dead) return;
    if (this.scene.time.now < this.busyUntil) return;

    const next = this.view.isMoving ? 'walk' : 'idle';
    if (next !== this.animation) this.play(next);
  }

  playAttack(): void {
    if (this.dead) return;
    this.play('attack');
    this.busyUntil = this.scene.time.now + 280;
  }

  playCast(): void {
    if (this.dead) return;
    this.play('cast');
    this.busyUntil = this.scene.time.now + 320;
  }

  playDeath(): void {
    if (this.dead) return;
    this.dead = true;

    this.barBackground.setVisible(false);
    this.barFill.setVisible(false);
    this.targetRing.setVisible(false);
    this.markIcon.setVisible(false);

    // O nome sai junto, e isso é o que mantém honesta a passada de `layoutCreatureLabels`:
    // a cena remove o sprite do conjunto no mesmo instante em que manda animar a morte, e
    // só adia o `destroy`. Deixar o nome desenhado durante esses 300 ms daria um rótulo
    // congelado — fora do desempilhamento, mas ainda na tela — bem em cima de quem ficou
    // vivo ao lado, que é a sobreposição de volta no exato instante do abate.
    this.label.setVisible(false);

    if (this.isPlayer && this.scene.anims.exists(`${this.textureKey}-death`)) {
      this.body.play(`${this.textureKey}-death`);
      return;
    }

    // Criatura não tem quadros de morte: some encolhendo, que custa nada de arte e
    // ainda assim marca o instante do abate.
    this.scene.tweens.add({
      targets: this.body,
      alpha: 0,
      scaleY: 0.3,
      duration: 260,
    });
  }

  private play(animation: CharacterAnimation | 'walk', force = false): void {
    this.animation = animation;

    const key = this.isPlayer
      ? characterAnimationKey(this.vocation, this.tier, animation, this.direction)
      : // A criatura agora tem coluna de conjuração própria (PRD §15.5): o `cast` deixou de
        // cair no quadro de ataque, que era o que fazia um Lich lançar magia dando um
        // bote de garra.
        creatureAnimationKey(this.vocation, animation, this.direction);

    if (!this.scene.anims.exists(key)) return;
    this.body.play(key, !force);
  }

  /**
   * Ajusta a cadência da caminhada à velocidade real do personagem.
   *
   * Sem isto, um personagem acelerado desliza com as pernas em ritmo de passeio — o
   * efeito de "patinar" que denuncia animação desacoplada do movimento.
   */
  setStepDuration(stepMs: number): void {
    if (stepMs <= 0) return;
    // Quatro quadros por passo.
    this.body.anims.msPerFrame = stepMs / 4;
  }

  /**
   * A barra some quando a criatura está intacta. Com várias criaturas na tela, barras
   * cheias em todo mundo viram ruído e escondem justamente a que importa.
   */
  setHealth(health: number, maxHealth: number): void {
    const ratio = maxHealth > 0 ? Math.max(0, Math.min(1, health / maxHealth)) : 0;
    const damaged = ratio < 1 && !this.dead;

    this.barBackground.setVisible(damaged);
    this.barFill.setVisible(damaged);
    if (!damaged) return;

    this.barFill.width = Math.max(1, BAR_WIDTH * ratio);
    this.barFill.setFillStyle(healthColor(ratio));
  }

  setTargeted(targeted: boolean): void {
    this.targetRing.setVisible(targeted && !this.dead);
  }

  /**
   * Marca de PvP (PRD §6).
   *
   * Precisa ser vista **antes** do golpe: a regra diz que atacar um desmarcado marca quem
   * ataca, e que atacar um marcado não marca ninguém. Sem o losango na tela, as duas
   * metades da regra só seriam descobertas depois de já valerem — que é a mesma classe de
   * defeito que §11 passou inteiro consertando.
   */
  setPvpMark(mark: PvpMark): void {
    if (mark === PvpMark.None) {
      this.markIcon.setVisible(false);
      return;
    }

    this.markIcon.setFillStyle(MARK_COLORS[mark]);
    this.markIcon.setVisible(!this.dead);
  }

  /** Balão de fala sobre a cabeça. Some sozinho — é fala, não legenda. */
  say(text: string): void {
    this.bubble?.destroy();
    this.bubbleTimer?.remove();

    this.bubble = this.scene.add.text(0, 0, text, {
      fontFamily: 'ui-monospace, monospace',
      fontSize: '11px',
      color: '#f4f7fa',
      backgroundColor: '#0b0f14cc',
      padding: { x: 4, y: 2 },
      align: 'center',
      wordWrap: { width: 160 },
    });
    this.bubble.setOrigin(0.5, 1);

    // Quatro segundos mais um por dez caracteres: uma frase longa precisa de mais tempo
    // na tela que um "oi", e um tempo fixo erraria nos dois casos.
    const lifetime = 4000 + text.length * 100;
    this.bubbleTimer = this.scene.time.delayedCall(lifetime, () => {
      this.bubble?.destroy();
      this.bubble = undefined;
    });

    this.sync();
  }

  /** Reposiciona tudo a partir da posição interpolada. */
  sync(): void {
    const { renderX, renderY } = this.view;
    const depth = depthFor(this.view.gridY);

    this.body.setPosition(renderX, renderY + TILE_SIZE / 2);
    this.body.setDepth(depth);

    // O topo do desenho: o sprite tem âncora no pé, então a cabeça está a uma altura de
    // corpo acima dela.
    const top = renderY + TILE_SIZE / 2 - this.artHeight;

    // Pilha acima da cabeça, empilhada de baixo para cima a partir da altura **real** do
    // nome: nome, barra, balão. Antes os três eram colocados por deslocamentos soltos
    // (`top - 2`, `top - 12`) que presumiam um nome de ~10 px; ele mede ~16 com o contorno,
    // e a barra caía 6 px dentro da caixa do texto, cortando o topo das letras — justo em
    // combate, que é quando a barra aparece e quando o nome importa.
    //
    // A âncora é o NOME, o único dos três que está sempre na tela: a barra some quando a
    // criatura está intacta (ver `setHealth`) e o balão é passageiro. Se a âncora fosse a
    // barra, o nome subiria ao primeiro golpe e desceria ao curar.
    const labelBottom = top - STACK_GAP - this.labelLift;
    const labelTop = labelBottom - this.labelHeight;
    const barCenter = labelTop - STACK_GAP - BAR_HEIGHT / 2;

    this.label.setPosition(renderX, labelBottom);
    this.label.setDepth(depth + 3);

    // À esquerda do nome, na altura do meio das letras: a marca é sobre a **pessoa**, e o
    // nome é onde a pessoa está escrita.
    this.markIcon.setPosition(
      renderX - this.label.width / 2 - MARK_SIZE,
      labelBottom - this.labelHeight / 2,
    );
    this.markIcon.setDepth(depth + 4);

    this.barBackground.setPosition(renderX, barCenter);
    this.barBackground.setDepth(depth + 2);

    // A barra encolhe pela direita: ancorá-la à esquerda é o que faz a perda de vida
    // parecer perda, e não uma barra que muda de lugar.
    this.barFill.setPosition(renderX - (BAR_WIDTH - this.barFill.width) / 2, barCenter);
    this.barFill.setDepth(depth + 3);

    this.targetRing.setPosition(renderX, renderY + TILE_SIZE / 2 - 3);
    this.targetRing.setDepth(depth - 1);

    this.bubble?.setPosition(renderX, barCenter - BAR_HEIGHT / 2 - STACK_GAP);
    this.bubble?.setDepth(depth + 5);
  }

  /**
   * O ponto do mundo cai sobre o corpo **desenhado**?
   *
   * O acerto é testado contra o retângulo do sprite, na posição interpolada em que ele
   * está sendo desenhado — e não contra o tile da grade, que era o que a cena fazia antes
   * (PRD §11.1, item 3). Os dois erravam de formas diferentes e somadas:
   *
   * - o sprite é mais alto que o tile e tem âncora na base (§8.1), então tudo o que passa
   *   dos 32 px — cabeça, ombros e o chapéu da vocação, mais de um terço do desenho —
   *   resolvia para o **tile de cima** e não selecionava nada;
   * - durante um passo (300 a 600 ms numa criatura) a posição de grade do último snapshot
   *   e o desenho ficam a até um tile de distância, então clicava-se onde a criatura
   *   visivelmente não estava.
   *
   * O resultado era um combate que parecia quebrado estando certo: o alvo se perdia a cada
   * clique, e perder o alvo cancela o auto-attack em silêncio.
   */
  containsPoint(worldX: number, worldY: number): boolean {
    if (this.dead) return false;
    return this.body.getBounds<Phaser.Geom.Rectangle>().contains(worldX, worldY);
  }

  /**
   * Sobe a pilha inteira — nome, barra, marca e balão juntos.
   *
   * Juntos, e não só o nome: uma barra de vida que ficasse para trás pareceria pertencer à
   * criatura de baixo, que é erro pior que o nome ilegível. Só guarda o valor; quem o
   * aplica é o `sync` do mesmo quadro.
   */
  setLabelLift(px: number): void {
    this.labelLift = px;
  }

  /**
   * Há rótulo a posicionar? Entidade recém-avistada ainda não recebeu o `EntityInfo`, e
   * quem está morrendo já teve o nome escondido — em nenhum dos dois casos há o que
   * empilhar, e medi-los daria degrau a um nome que ninguém vê.
   */
  get hasLabel(): boolean {
    return this.label.visible && this.label.text !== '';
  }

  /** Meia-largura da PILHA: um nome curto é mais estreito que a barra, que sobe junto. */
  get labelHalfWidth(): number {
    return Math.max(this.label.width, BAR_WIDTH) / 2;
  }

  /**
   * Altura de um degrau da pilha: nome, barra e os dois respiros.
   *
   * É por sprite porque a altura do nome é medida, mas na prática dá o mesmo número para
   * todos — a fonte do rótulo é uma só.
   */
  get labelRowHeight(): number {
    return this.labelHeight + BAR_HEIGHT + STACK_GAP * 2;
  }

  /**
   * Profundidade de desenho atual. Com dois sprites sobrepostos — o caso comum de um
   * personagem colado no de trás —, quem está na frente é quem o clique deve pegar.
   */
  get depth(): number {
    return this.body.depth;
  }

  /** Objeto que a câmera deve seguir. */
  get cameraTarget(): Phaser.GameObjects.Sprite {
    return this.body;
  }

  destroy(): void {
    this.body.destroy();
    this.label.destroy();
    this.barBackground.destroy();
    this.barFill.destroy();
    this.targetRing.destroy();
    this.markIcon.destroy();
    this.bubble?.destroy();
    this.bubbleTimer?.remove();
  }
}

type LabelBox = {
  sprite: CreatureSprite;
  /** Centro horizontal em pixels de mundo, tirado da GRADE. */
  centerX: number;
  /** Base da pilha em pixels de mundo, antes de subir. */
  baseY: number;
  halfWidth: number;
  /** Saída: em que degrau esta pilha ficou. */
  row: number;
};

/**
 * Reaproveitado entre quadros: a passada roda a cada quadro e não deve alocar.
 *
 * Vive no módulo, e não na cena, porque é detalhe da distribuição — e como o desenho é um
 * só, não há duas passadas concorrentes que pudessem disputá-lo.
 */
const labelBoxes: LabelBox[] = [];

/**
 * Impede que rótulos de entidades vizinhas se escrevam por cima ("RatoProvadorLobo").
 *
 * Nenhum sprite consegue resolver isto sozinho: cada um posiciona o próprio nome a partir
 * da própria posição, todos na mesma altura, e um nome de 10 px monoespaçado passa dos 80
 * px enquanto o tile tem 32 — dois vizinhos colidem sempre, e criaturas se juntam
 * justamente em combate. Por isso a decisão é do conjunto, e mora aqui, num único ponto
 * por quadro que a cena chama depois de mover as entidades e antes de sincronizá-las.
 *
 * Quem colide sobe um degrau, e cada degrau vale um nome mais uma barra. A ordem é da
 * frente para trás — mesma ordem da profundidade de desenho —, então "quanto mais alto o
 * nome, mais atrás a criatura" bate com o que se vê.
 *
 * Usa a posição de **grade**, não a interpolada, pela mesma razão de `depthFor`: com o
 * pixel, o degrau trocaria no meio de um passo e o nome tremeria a 60 fps. Com a grade, ele
 * muda uma vez por passo, junto com a profundidade.
 *
 * Custo: uma ordenação de n = entidades na Área de Interesse (dezenas), mais uma varredura
 * com janela. Como um degrau (~23 px) é menor que um tile (32 px), uma pilha só alcança
 * quem está a poucos degraus de distância vertical, e o índice `first` descarta o resto sem
 * testar — o par de laços não vira quadrático num aglomerado espalhado.
 */
export function layoutCreatureLabels(sprites: Iterable<CreatureSprite>): void {
  let count = 0;

  for (const sprite of sprites) {
    // A regra é que o conjunto medido aqui seja o mesmo que aparece na tela. Quem morre sai
    // dos dois no mesmo instante: a cena tira o sprite do mapa e `playDeath` esconde o
    // nome, então o abate não deixa rótulo órfão para os vizinhos re-fluírem por cima.
    if (!sprite.hasLabel) continue;

    const box = (labelBoxes[count] ??= {
      sprite,
      centerX: 0,
      baseY: 0,
      halfWidth: 0,
      row: 0,
    });

    box.sprite = sprite;
    box.centerX = sprite.view.gridX * TILE_SIZE + TILE_SIZE / 2;
    box.baseY = sprite.view.gridY * TILE_SIZE;
    box.halfWidth = sprite.labelHalfWidth;
    box.row = 0;
    count += 1;
  }

  labelBoxes.length = count;
  if (count === 0) return;

  // O desempate por x mantém o resultado determinístico: sem ele, a ordem de iteração do
  // chamador decidiria quem sobe, e dois quadros seguidos poderiam trocar.
  labelBoxes.sort((a, b) => b.baseY - a.baseY || a.centerX - b.centerX);

  // Um degrau só, tirado do primeiro: degraus de alturas diferentes deixariam a pilha
  // torta entre criaturas, e a fonte do rótulo é a mesma para todas.
  const rowHeight = labelBoxes[0]!.sprite.labelRowHeight;
  const reach = rowHeight * (MAX_LABEL_ROWS + 1);
  let first = 0;

  for (let i = 0; i < count; i += 1) {
    const box = labelBoxes[i]!;
    while (first < i && labelBoxes[first]!.baseY - box.baseY > reach) first += 1;

    let row = 0;
    while (row < MAX_LABEL_ROWS) {
      if (!collides(first, i, box, box.baseY - row * rowHeight, rowHeight)) break;
      row += 1;
    }

    box.row = row === MAX_LABEL_ROWS ? 0 : row;
    box.sprite.setLabelLift(box.row * rowHeight);
  }
}

/** Este degrau esbarra em algum rótulo já colocado da janela [from, to)? */
function collides(
  from: number,
  to: number,
  box: LabelBox,
  y: number,
  rowHeight: number,
): boolean {
  for (let j = from; j < to; j += 1) {
    const other = labelBoxes[j]!;
    const otherY = other.baseY - other.row * rowHeight;
    if (Math.abs(otherY - y) >= rowHeight) continue;
    if (Math.abs(other.centerX - box.centerX) >= other.halfWidth + box.halfWidth + LABEL_GAP_X) {
      continue;
    }
    return true;
  }
  return false;
}

function healthColor(ratio: number): number {
  if (ratio > 0.5) return 0x5cb85c;
  if (ratio > 0.2) return 0xe0a33e;
  return 0xd9534f;
}
